// Custom fetch client for orval-generated code
// This handles base URL and can be extended for auth headers, etc.

import { REQUEST_TIMEOUT_MS } from "../constants";
import { ApiError } from "../resources/ApiError";
import { coerceWireValues } from "./coerce";

// Using null instead of capturing fetch at module load time allows MSW to intercept
let fetchImpl: typeof fetch | null = null;
let baseUrlOverride: string | null = null;
let defaultHeaders: (() => Record<string, string>) | null = null;
let credentialsMode: RequestCredentials = "include";

const TRAILING_SLASH_RE = /\/$/;

export interface ApiClientConfig {
  baseUrl?: string;
  fetch?: typeof fetch;
  defaultHeaders?: () => Record<string, string>;
  credentials?: RequestCredentials;
}

/**
 * Configure the API client. Any omitted field is left unchanged.
 * Must be called with a `baseUrl` before making any requests.
 */
export function setConfig(config: ApiClientConfig) {
  if (config.baseUrl !== undefined) baseUrlOverride = config.baseUrl;
  if (config.fetch !== undefined) fetchImpl = config.fetch;
  if (config.defaultHeaders !== undefined) defaultHeaders = config.defaultHeaders;
  if (config.credentials !== undefined) credentialsMode = config.credentials;
}

function getBaseUrl() {
  if (baseUrlOverride === null) {
    if (process.env.NODE_ENV !== "test") {
      console.warn("API base URL not set — call setConfig({ baseUrl }) before making requests");
    }
    return "";
  }
  return baseUrlOverride.replace(TRAILING_SLASH_RE, "");
}

/** `Retry-After` as milliseconds: a number of seconds, or an HTTP date; anything else is no answer. */
export const retryAfterMs = (header: string | null, now = Date.now()): number | undefined => {
  if (!header) return;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds * 1000);
  const at = Date.parse(header);
  return Number.isNaN(at) ? undefined : Math.max(0, at - now);
};

const failed = (error: ApiError, response: Response) => {
  error.retryAfterMs = retryAfterMs(response.headers.get("retry-after"));
  return error;
};

export const customFetch = async <T>(url: string, options?: RequestInit): Promise<T> => {
  const baseUrl = getBaseUrl();
  const fullUrl = `${baseUrl}${url}`;
  const fetchFn = fetchImpl ?? globalThis.fetch;

  // FormData needs the browser-generated `multipart/form-data; boundary=...` header;
  // forcing `application/json` here ships multipart bodies with the wrong content type
  // and the backend rejects with "Unsupported media type 'application/json' in request".
  const isFormData = typeof FormData !== "undefined" && options?.body instanceof FormData;

  const response = await fetchFn(fullUrl, {
    ...options,
    signal: options?.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    credentials: options?.credentials ?? credentialsMode,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...defaultHeaders?.(),
      ...options?.headers,
    },
  });

  if (response.status === 204) {
    if (!response.ok) {
      throw failed(ApiError.fromResponseData(undefined, response.status, options?.method, url), response);
    }
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");

  // Binary bodies (e.g. the roster PDF) must stay bytes: reading them as text corrupts them.
  if (!isJson && response.ok && !contentType.startsWith("text/")) {
    return (await response.blob()) as T;
  }

  if (!isJson) {
    const text = await response.text();
    if (!response.ok) {
      throw failed(
        ApiError.fromResponseData({ message: text || response.statusText }, response.status, options?.method, url),
        response,
      );
    }
    return text as T;
  }

  const data = await response.json();

  if (!response.ok) {
    throw failed(ApiError.fromResponseData(data, response.status, options?.method, url), response);
  }

  return coerceWireValues(data) as T;
};
